import { ClassValue, clsx } from 'clsx';
import { CSSProperties, useState } from 'react';
import classNames from './Image.module.css';

export type ImageSource = { uri?: string };

export interface ImageProps {
  source?: ImageSource | null;
  className?: ClassValue;
  /** Per-instance values only; style with `className`. */
  style?: CSSProperties;
  /** Grow to fill the parent along both axes. */
  fill?: boolean;
  resizeMode?: 'cover' | 'contain';
  onLoad?: VoidFunction;
  onError?: VoidFunction;
  /** Omit for decorative images. */
  alt?: string;
  testID?: string;
}

export function Image({
  source,
  className,
  style,
  fill,
  resizeMode = 'cover',
  onLoad,
  onError,
  alt,
  testID,
}: ImageProps) {
  const [failedUri, setFailedUri] = useState<string>();

  const classes = clsx(
    classNames.image,
    fill && classNames.fill,
    resizeMode === 'contain' && classNames.contain,
    className,
  );

  const uri = source?.uri;
  const hasFailed = !!uri && uri === failedUri;

  const handleError = () => {
    setFailedUri(uri);
    onError?.();
  };

  // An `<img>` with no source or a failed one shows the browser's broken-image icon, where React
  // Native Web showed nothing. Keep the box, its size and background, without the icon.
  if (!uri || hasFailed) {
    return <div className={classes} style={style} data-testid={testID} />;
  }

  return (
    <img
      className={classes}
      style={style}
      src={uri}
      alt={alt ?? ''}
      onLoad={onLoad}
      onError={handleError}
      data-testid={testID}
    />
  );
}
